/**
 * PianoLogic.js
 * Specialized logic for piano voicing and performance.
 * Simulates two-handed playing: Left Hand (Bass/Shell) vs Right Hand (Melody/Comp).
 */

// Middle C = 60. Split point usually around F3 (53) or G3 (55).
const SPLIT_POINT = 55;

/**
 * Splits a list of MIDI note numbers into Left Hand and Right Hand groups.
 * @param {Array} notes - Array of MIDI note numbers.
 * @returns {Object} { lh: [], rh: [] }
 */
export const splitPianoVoicing = (notes) => {
    if (!notes || notes.length === 0) return { lh: [], rh: [] };

    const sorted = [...notes].sort((a,b) => a - b);
    const root = sorted[0]; // Lowest note usually Bass/Root

    const lh = [];
    const rh = [];

    // 1. Force Root to Left Hand
    lh.push(root);

    // 2. Distribute remainder
    for (let i = 1; i < sorted.length; i++) {
        const note = sorted[i];
        if (note < SPLIT_POINT) {
            lh.push(note);
        } else {
            rh.push(note);
        }
    }

    // 3. Balancing Constraints
    // Left Hand Constraints:
    // Ideally 1-3 notes (Root, 5th, 7th). Interval limit (10th/1.3 octaves).
    // If LH is clustered, move top notes to RH.

    // Right Hand Constraints:
    // Max 4-5 notes. If too many, drop lower RH notes (thickeners) to prioritize Melody (top).

    // Simple Re-balance: If LH has too many notes close to split, push up
    // Or if RH is empty, push highest LH up?
    if (rh.length === 0 && lh.length > 1) {
        rh.push(lh.pop());
    }

    return { lh, rh };
};

/**
 * Applies a pianist performance pattern to the voiced event.
 * Replaces a block chord event with multiple events simulating LH/RH action.
 * @param {Object} event - The source abstract event { s, d, len, vel, notes: [] }
 * @param {string} style - 'stride', 'broken', 'block', 'arpeggio'
 */
export const applyPianoPattern = (event, style = 'block') => {
    const notes = event.notes || [event.d];
    const { lh, rh } = splitPianoVoicing(notes);
    const newEvents = [];

    // Base properties
    // Fix: Propagate all event properties (including barIndex, tension) to ensure downstream integrity
    const baseParams = { ...event };
    delete baseParams.notes; // Prevent recursion/confusion
    delete baseParams.d;     // Will be overwritten
    delete baseParams.s;     // Will be overwritten
    delete baseParams.len;   // Will be overwritten
    delete baseParams.vel;   // Will be overwritten

    if (style === 'stride') {
        // Beat 1/3: LH Bass (Root only or low 5th)
        // Beat 2/4: LH Chord (Rest of LH) + RH Chord
        // Simplified: Low LH on Onset. High LH + RH delayed?

        // Split LH into Bass (lowest) and Tenor (rest)
        const bassNote = lh[0];
        const tenorNotes = lh.slice(1);

        // Bass Hit
        newEvents.push({ ...baseParams, s: event.s, d: bassNote, len: 0.4, vel: event.vel });

        // Chord Hit (Tenor + RH) - Delayed by 1 beat?
        // No, stride is usually: Bass on 1, Chord on 2.
        // If this event is 1 beat long, we can't do full stride.
        // If event is long (2-4 beats), we can stride.

        if (event.len >= 2.0) {
             // Stride Pattern
             const chordTime = event.s + 1.0;
             [...tenorNotes, ...rh].forEach(n => {
                 newEvents.push({ ...baseParams, s: chordTime, d: n, len: 0.4, vel: event.vel * 0.9 });
             });
             // Bass on 3?
             if (event.len >= 3.0) {
                 newEvents.push({ ...baseParams, s: event.s + 2.0, d: bassNote, len: 0.4, vel: event.vel });
             }
             // Chord on 4?
             if (event.len >= 4.0) {
                 [...tenorNotes, ...rh].forEach(n => {
                     newEvents.push({ ...baseParams, s: event.s + 3.0, d: n, len: 0.4, vel: event.vel * 0.9 });
                 });
             }
        } else {
            // Short event: Just break it slightly (Bass then Chord)
            // Flam: Bass at 0, Chord at 0.05
            [...tenorNotes, ...rh].forEach(n => {
                 newEvents.push({ ...baseParams, s: event.s + 0.05, d: n, len: event.len, vel: event.vel });
            });
        }

    } else if (style === 'broken') {
        // Arpeggiate Up: LH -> RH
        // 16th note triplets or 16ths?
        const allNotes = [...lh, ...rh];
        allNotes.forEach((n, i) => {
            newEvents.push({
                ...baseParams,
                s: event.s + (i * 0.05), // Fast roll
                d: n,
                len: event.len, // Sustain
                vel: event.vel
            });
        });

    } else if (style === 'rock') {
        // LH Octaves pulsing, RH Block
        // Assume 4/4 pulsing
        const bassNote = lh[0];

        // Pulse LH 8th notes
        const pulses = Math.floor(event.len / 0.5);
        for(let i=0; i<pulses; i++) {
            newEvents.push({
                ...baseParams,
                s: event.s + (i * 0.5),
                d: bassNote,
                len: 0.25,
                vel: (i%2===0) ? event.vel : event.vel * 0.8
            });
            // Octave?
            newEvents.push({
                ...baseParams,
                s: event.s + (i * 0.5),
                d: bassNote + 12,
                len: 0.25,
                vel: (i%2===0) ? event.vel : event.vel * 0.8
            });
        }

        // RH Block on Downbeats? Or sustained?
        // Let's sustain RH
        rh.forEach(n => {
            newEvents.push({ ...baseParams, s: event.s, d: n, len: event.len, vel: event.vel * 0.9 });
        });

    } else {
        // Block (Default) - Hands slightly offset
        // LH on grid, RH slightly late (human)
        lh.forEach(n => {
            newEvents.push({ ...baseParams, s: event.s, d: n, len: event.len, vel: event.vel });
        });
        rh.forEach(n => {
            newEvents.push({ ...baseParams, s: event.s + 0.02, d: n, len: event.len, vel: event.vel * 0.95 });
        });
    }

    return newEvents;
};
